import {
  collection,
  doc,
  orderBy,
  query,
  where,
  QueryConstraint,
  getDocs,
  getDoc,
  getCountFromServer,
  runTransaction,
} from 'firebase/firestore';
import { FiltersType } from 'modules/dashboard/pages/GameEventsScreen/types';

import { firestore } from '../Firebase';
import { GameEvent, GameEventLevel, GameEventLevelPuzzle } from '../../types/components/GameEvent';

export type getAllGameEventsParams = {
  dateSort?: 'asc' | 'desc';
  filters?: FiltersType[];
};

export const getAllGameEvents = async (params: getAllGameEventsParams) => {
  const { dateSort, filters } = params;

  const whereParams: Array<QueryConstraint> = [];

  if (filters?.length) {
    filters.forEach((filter) =>
      whereParams.push(where(filter.field, filter.operator, filter.value)),
    );
  }

  const q = query(
    collection(firestore, 'gameEvents'),
    ...whereParams,
    orderBy('startDate', dateSort ?? 'desc'),
  );
  const querySnapshot = await getDocs(q);
  const data: GameEvent[] = [];
  for (let i = 0; i < querySnapshot.docs.length; i++) {
    const doc = querySnapshot.docs[i];

    const {
      name,
      published,
      difficulty,
      image,
      entryFee,
      startDate,
      endDate,
      createdAt,
      updatedAt,
    } = doc.data();

    const event = {
      id: doc.id,
      name,
      published,
      difficulty,
      image,
      entryFee,
      startDate: startDate?.toDate(),
      endDate: endDate?.toDate(),
      eventLevelsCount: 0,
      createdAt: createdAt ? createdAt.toDate() : null,
      updatedAt: updatedAt ? updatedAt.toDate() : null,
      levels: [],
    };

    //Aggregate levels count.
    const countRes = await getCountFromServer(
      query(collection(firestore, `gameEvents/${event.id}/levels`)),
    );
    event.eventLevelsCount = countRes.data().count ?? 0;

    data.push(event);
  }
  return data;
};

export const deleteGameEventById = async (id: string) => {
  return await runTransaction(firestore, async (tx) => {
    //Remove levels subcollection
    const gameEventsLevelsQuery = query(collection(firestore, `gameEvents/${id}/levels`));
    const gameEventsLevelsQuerySnapshoot = await getDocs(gameEventsLevelsQuery);
    const gameEventsLevelsDocs = gameEventsLevelsQuerySnapshoot?.docs;
    if (gameEventsLevelsDocs?.length) {
      for (let i = 0; i < gameEventsLevelsDocs.length; i++) {
        const levelId = gameEventsLevelsDocs[i]?.id;
        if (!levelId) continue;
        await tx.delete(doc(firestore, `gameEvents/${id}/levels/`, levelId));
        //Remove puzzles subcollection
        const puzzlesQuery = query(
          collection(firestore, `gameEvents/${id}/levels/${levelId}/puzzles`),
        );
        const puzzlesQuerySnapshoot = await getDocs(puzzlesQuery);
        const puzzlesDocs = puzzlesQuerySnapshoot?.docs;
        for (let j = 0; j < puzzlesDocs.length; j++) {
          const puzzleId = puzzlesDocs[j]?.id;
          if (!puzzleId) continue;
          await tx.delete(doc(firestore, `gameEvents/${id}/levels/${levelId}/puzzles`, puzzleId));
        }
      }
    }
    //Remove main gameEvent doc
    return await tx.delete(doc(firestore, 'gameEvents', id));
  });
};

export const setGameEventData = async (data: GameEvent) => {
  const gameEventsRef = collection(firestore, 'gameEvents');
  const gameEventId = data.id;

  const { levels, ...gameEvent } = data;
  await runTransaction(firestore, async (transaction) => {
    transaction.set(doc(gameEventsRef, gameEventId), gameEvent);

    const levelsRef = collection(firestore, `gameEvents/${gameEventId}/levels`);
    const assignedLevels = await getDocs(levelsRef);

    for (const assignedLevel of assignedLevels.docs) {
      const shouldBeRemoved = levels.some((level) => level.id !== assignedLevel.id);

      if (shouldBeRemoved) {
        transaction.delete(doc(levelsRef, assignedLevel.id));
      }
    }

    return Promise.all(
      levels.map(async (level) => {
        const levelsRef = collection(firestore, `gameEvents/${gameEventId}/levels`);
        const levelId = level.id || doc(levelsRef).id;

        const { puzzles, ...levelData } = level;

        transaction.set(doc(levelsRef, levelId), levelData);

        const puzzlesRef = collection(
          firestore,
          `gameEvents/${gameEventId}/levels/${levelId}/puzzles`,
        );
        const assignedPuzzles = await getDocs(puzzlesRef);

        for (const assignedPuzzle of assignedPuzzles.docs) {
          const shouldBeRemoved = puzzles.some((puzzle) => puzzle.puzzleId !== assignedPuzzle.id);

          if (shouldBeRemoved) {
            transaction.delete(doc(puzzlesRef, assignedPuzzle.id));
          }
        }

        puzzles.map((puzzle) => {
          const puzzleId = puzzle.puzzleId;

          transaction.set(doc(puzzlesRef, puzzleId), puzzle);
        });
      }),
    );
  });
};

export const getGameEventById = async (id: string): Promise<GameEvent | null> => {
  const docRef = doc(firestore, 'gameEvents', id);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists) {
    return null;
  }

  const levelsRef = collection(firestore, `gameEvents/${id}/levels`);
  const levelsData = await getDocs(levelsRef);

  const levels = await Promise.all(
    levelsData.docs.map(async (level) => {
      const puzzlesRef = collection(firestore, `gameEvents/${id}/levels/${level.id}/puzzles`);
      const puzzles = await getDocs(puzzlesRef);

      return {
        id: level.id,
        ...level.data(),
        puzzles: puzzles.docs.map((puzzles) => puzzles.data() as GameEventLevelPuzzle),
      } as GameEventLevel;
    }),
  );

  const gameEventData = docSnap.data();

  return {
    id: docSnap.id,
    ...gameEventData,
    startDate: gameEventData?.startDate?.toDate(),
    endDate: gameEventData?.endDate?.toDate(),
    levels,
  } as GameEvent;
};
